/**
 * <secondary-nav>
 *
 * Lightweight controller for the header's secondary navigation row. It only
 * detects horizontal overflow and toggles a `data-overflow` attribute, which
 * CSS uses to left-align the list and reveal the right-edge fade hint.
 *
 * Implemented as a plain custom element (no theme base class) so it has zero
 * coupling and re-initialises automatically whenever the header re-renders via
 * the Section Rendering API.
 */
class SecondaryNav extends HTMLElement {
  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    this.#scroller = this.querySelector('.secondary-nav__scroll');
    if (!this.#scroller) return;

    this.#checkOverflow();

    this.#resizeObserver = new ResizeObserver(() => this.#checkOverflow());
    this.#resizeObserver.observe(this);
    this.#resizeObserver.observe(this.#scroller);

    // Desktop wheel-to-horizontal translation. On a horizontally-overflowing strip
    // the browser only scrolls sideways from a horizontal wheel input (shift-scroll
    // or a trackpad's horizontal gesture); a plain vertical wheel scrolls the page.
    // If the strip is overflowing and the wheel event is predominantly VERTICAL
    // (mouse wheel), redirect it to horizontal scroll on the strip. If the event
    // is already horizontal (trackpad), leave it alone — the browser handles it.
    this.#scroller.addEventListener('wheel', this.#handleWheel, { passive: false });

    // Re-check once the row has settled (web fonts can change measured width).
    requestAnimationFrame(() => this.#checkOverflow());
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#scroller?.removeEventListener('wheel', this.#handleWheel);
  }

  #handleWheel = (event) => {
    if (!this.hasAttribute('data-overflow')) return;
    // Vertical-dominant wheel input → redirect to horizontal scroll.
    // Trackpad horizontal gestures come through with |deltaX| > |deltaY| and are left alone.
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
    event.preventDefault();
    this.#scroller.scrollLeft += event.deltaY;
  };

  #checkOverflow = () => {
    const scroller = this.#scroller;
    if (!scroller) return;

    const hasOverflow = scroller.scrollWidth > scroller.clientWidth + 1;
    this.toggleAttribute('data-overflow', hasOverflow);
  };
}

if (!customElements.get('secondary-nav')) {
  customElements.define('secondary-nav', SecondaryNav);
}